import { MetaState } from '../meta/meta'
import { CHARACTER_LIST } from './characters'
import { MAP_LIST } from './maps'

/** Summary of the run that just ended, for run-specific achievement checks. */
export interface AchRun {
  time: number
  level: number
  kills: number
  bosses: number
  victory: boolean
  difficultyId: string
  mapId: string
}

export interface AchDef {
  id: string
  name: string // English; IT via i18n tName('ach', id, name)
  desc: string // English; IT via i18n tBlurb('ach', id, desc)
  icon: string
  check: (meta: MetaState, run: AchRun) => boolean
  /** Gold banked when unlocked (defaults to 50 if omitted). */
  reward?: number
}

const hardish = (d: string) => d === 'hard' || d === 'cooked'

export const ACHIEVEMENTS: AchDef[] = [
  { id: 'first_blood', name: 'First Blood', desc: 'Kill your first enemy.', icon: '🩸', check: (m) => m.totalKills >= 1 },
  { id: 'centurion', name: 'Centurion', desc: 'Kill 100 enemies in total.', icon: '⚔️', check: (m) => m.totalKills >= 100 },
  { id: 'slayer', name: 'Slayer', desc: 'Kill 1,000 enemies in total.', icon: '💀', check: (m) => m.totalKills >= 1000 },
  { id: 'exterminator', name: 'Exterminator', desc: 'Kill 10,000 enemies in total.', icon: '☠️', check: (m) => m.totalKills >= 10000, reward: 300 },
  { id: 'survivor', name: 'Survivor', desc: 'Survive 5 minutes in a run.', icon: '⏳', check: (m) => m.bestTime >= 300 },
  { id: 'marathoner', name: 'Marathoner', desc: 'Survive 10 minutes in a run.', icon: '🏃', check: (m) => m.bestTime >= 600 },
  { id: 'level10', name: 'Getting Strong', desc: 'Reach level 10 in a run.', icon: '🔟', check: (m) => m.bestLevel >= 10 },
  { id: 'level20', name: 'Overpowered', desc: 'Reach level 20 in a run.', icon: '💪', check: (m) => m.bestLevel >= 20 },
  { id: 'level30', name: 'Unstoppable', desc: 'Reach level 30 in a run.', icon: '🌟', check: (m) => m.bestLevel >= 30 },
  { id: 'boss1', name: 'Giant Slayer', desc: 'Defeat your first boss.', icon: '🗡️', check: (m) => m.bossesKilled >= 1 },
  { id: 'boss10', name: 'Boss Hunter', desc: 'Defeat 10 bosses in total.', icon: '🏹', check: (m) => m.bossesKilled >= 10 },
  { id: 'boss25', name: 'Bane of Bosses', desc: 'Defeat 25 bosses in total.', icon: '👹', check: (m) => m.bossesKilled >= 25 },
  { id: 'clear_meadow', name: 'Meadow Cleared', desc: 'Beat Tranquility Flats.', icon: '🌳', check: (m) => m.clearedMaps.includes('meadow') },
  { id: 'clear_dunes', name: 'Dunes Cleared', desc: 'Beat The Rust Dunes.', icon: '🏜️', check: (m) => m.clearedMaps.includes('dunes') },
  { id: 'clear_graveyard', name: 'Graveyard Cleared', desc: 'Beat Hulk Drift.', icon: '🪦', check: (m) => m.clearedMaps.includes('graveyard') },
  { id: 'clear_rift', name: 'Rift Cleared', desc: 'Beat The Void Maw.', icon: '🌀', check: (m) => m.clearedMaps.includes('rift') },
  { id: 'world_tour', name: 'World Tour', desc: 'Clear every map.', icon: '🗺️', check: (m) => MAP_LIST.every((mp) => m.clearedMaps.includes(mp.id)), reward: 400 },
  { id: 'hard_clear', name: 'Hard Mode', desc: 'Clear any map on Hard or above.', icon: '🔥', check: (_m, r) => r.victory && hardish(r.difficultyId) },
  { id: 'cooked_clear', name: 'Well Done', desc: 'Clear any map on Cooked.', icon: '🌶️', check: (_m, r) => r.victory && r.difficultyId === 'cooked', reward: 250 },
  { id: 'rich', name: 'Loaded', desc: 'Earn 1,000 gold in total.', icon: '🪙', check: (m) => m.totalGoldEarned >= 1000 },
  { id: 'tycoon', name: 'Tycoon', desc: 'Earn 5,000 gold in total.', icon: '💰', check: (m) => m.totalGoldEarned >= 5000 },
  { id: 'first_evo', name: 'Evolved', desc: 'Evolve a weapon.', icon: '✨', check: (m) => m.evolvedTotal >= 1 },
  { id: 'evo_master', name: 'Apex', desc: 'Evolve 10 weapons in total.', icon: '🌈', check: (m) => m.evolvedTotal >= 10, reward: 250 },
  { id: 'recruiter', name: 'Recruiter', desc: 'Unlock 8 characters.', icon: '🧑‍🤝‍🧑', check: (m) => m.unlockedCharacters.length >= 8 },
  { id: 'full_roster', name: 'Full House', desc: 'Unlock every character.', icon: '🎭', check: (m) => m.unlockedCharacters.length >= CHARACTER_LIST.length, reward: 500 },
  { id: 'regular', name: 'Regular', desc: 'Play 10 runs.', icon: '🎮', check: (m) => m.totalRuns >= 10 },
  { id: 'addicted', name: 'Addicted', desc: 'Play 50 runs.', icon: '🕹️', check: (m) => m.totalRuns >= 50, reward: 300 },
  // v11 — endgame quests
  { id: 'level40', name: 'Ascended', desc: 'Reach level 40 in a run.', icon: '🔆', check: (m) => m.bestLevel >= 40, reward: 300 },
  { id: 'kills25k', name: 'Annihilator', desc: 'Kill 25,000 enemies in total.', icon: '💥', check: (m) => m.totalKills >= 25000, reward: 400 },
  { id: 'boss50', name: 'Boss Legend', desc: 'Defeat 50 bosses in total.', icon: '👑', check: (m) => m.bossesKilled >= 50, reward: 400 },
  { id: 'veteran', name: 'Veteran', desc: 'Play 100 runs.', icon: '🎖️', check: (m) => m.totalRuns >= 100, reward: 400 },
]

export const ACHIEVEMENT_MAP: Record<string, AchDef> = {}
for (const a of ACHIEVEMENTS) ACHIEVEMENT_MAP[a.id] = a

/** Returns the achievement defs newly unlocked by this run (and mutates meta.achievements). */
export function evaluateAchievements(meta: MetaState, run: AchRun): AchDef[] {
  const newly: AchDef[] = []
  for (const a of ACHIEVEMENTS) {
    if (meta.achievements.includes(a.id)) continue
    if (a.check(meta, run)) {
      meta.achievements.push(a.id)
      newly.push(a)
    }
  }
  return newly
}
